/*
 * Mobile search row + pill.
 *
 * The header's search row gives up its space once the page scrolls; the magnifier that sits with
 * the other icons puts it back in place, so reaching search never means scrolling to the top of a
 * long collection.
 *
 * WHY THIS NO LONGER ANIMATES A SIZE
 * The header section is `position: sticky`, so it is still IN FLOW (assets/theme.css:7122-7127)
 * with no spacer. Both earlier versions collapsed the row by animating its box - first max-height,
 * then its real measured height - and that is what made the whole thing feel broken, for three
 * reasons no amount of easing or measuring could reach:
 *
 *   1. Shrinking an in-flow box at the top of the document re-lays out everything below it, once
 *      per animation frame. Chrome and Firefox hide that with scroll anchoring, but they pay for
 *      it by subtracting the shrink (~60px) from window.scrollY - the very number the collapse
 *      threshold was compared against. With a single threshold and no hysteresis the class came
 *      straight back off, the row re-opened, scrollY went back up, and it flipped again. Measured
 *      in a faithful repro: 13-27 class flips on ONE slow scroll, each reversing a transition that
 *      was still running.
 *   2. WebKit does not implement scroll anchoring, so on iOS nothing compensated: the page content
 *      was dragged ~60px upwards during each collapse (118px measured over one down-and-up cycle)
 *      on top of whatever the finger was doing.
 *   3. .header__search lives inside <header>, and the base theme observes <header> with a ResizeObserver
 *      (assets/header.js:25). Every frame of the animation delivered a callback into setHeight()
 *      (assets/header.js:59-71), which forces a full-document layout and rewrites four inherited
 *      custom properties on :root. Measured: 37-42 callbacks per scroll. --header-height is what
 *      .sticky-element's `top` is built from (assets/theme.css:1539-1543), and that element
 *      carries `transition: all .5s`, so the collection toolbar restarted a half-second ease on
 *      every one of those writes and wobbled along behind the header for ~500ms after it stopped.
 *
 * So the row keeps its grid track in every state. What collapses is an out-of-flow background
 * panel, and what fades is the row's own content - see the mobile block in sections/header.liquid.
 * The header's border box is constant, which removes all three mechanisms at once. Measured after
 * the change: 0 ResizeObserver callbacks, 0px of content displacement with scroll anchoring either
 * on or off, 1 class toggle per direction.
 *
 * What is left for this file: publish the row's height, and own the collapsed/expanded state with
 * enough hysteresis that nothing can flip it mid-animation.
 */
(() => {
  const EXPANDED = 'header--search-expanded';
  const COLLAPSED = 'header--search-collapsed';
  const NO_TRANSITION = 'header--search-no-transition';
  const ROOT_COLLAPSED = 'header-search-collapsed'; // on <html>, for offsets outside the header

  // Must stay in step with the bottom bar's transform transition in
  // sections/header.liquid (0.3s); the row's own fade finishes inside it.
  const DURATION = 300;

  const mobile = window.matchMedia('(max-width: 749.98px)');
  const controllers = new WeakMap();

  // The full placeholder ("Search part number, brand or keyword") clips inside the field on
  // 380px-and-narrower screens. A placeholder cannot respond to media queries, so the input
  // carries both strings and we swap the attribute at the breakpoint.
  const narrow = window.matchMedia('(max-width: 380px)');
  /* The 750-989 band USED to run the short placeholder too, from a time when
     its pill was much narrower. It is not any more: the right cluster's labels
     now give way at 900 and the pill takes the room, so the full string fits
     across the whole band with margin (measured at its tightest, 902px: 246px
     of text room against 238px of text). Owen asked for the full wording back
     there, 2026-08-09. Only the ≤380px phone case still needs the short one. */
  const applyPlaceholders = () => {
    document.querySelectorAll('input[data-placeholder-short]').forEach((input) => {
      if (!input.dataset.placeholderFull) input.dataset.placeholderFull = input.placeholder;
      const short = input.dataset.placeholderShort;
      const wantShort = narrow.matches;
      // Guard against an untranslated locale rendering Shopify's "Translation missing" string.
      const usable = short && !/translation missing/i.test(short);
      input.placeholder = wantShort && usable ? short : input.dataset.placeholderFull;
    });
  };
  narrow.addEventListener('change', applyPlaceholders);
  document.addEventListener('shopify:section:load', applyPlaceholders);
  // Some environments (notably emulated viewports) resize without firing MediaQueryList
  // change events; a debounced resize listener catches those and device rotation.
  let placeholderResizeTimer = null;
  window.addEventListener('resize', () => {
    clearTimeout(placeholderResizeTimer);
    placeholderResizeTimer = setTimeout(applyPlaceholders, 150);
  });
  applyPlaceholders(); // script is deferred, so the DOM is parsed by now


  const createController = (header) => {
    const search = header.querySelector('.header__search');
    if (!search) return null;
    const section = header.closest('.header-section') || header.parentElement;

    const pills = [];
    let rowHeight = 0;
    let collapsed = false;
    let ticking = false;
    let latchedUntil = 0;
    let lastWidth = window.innerWidth;
    let lastY = window.scrollY;

    // Scroll position at which the header section pins. While it is unstuck,
    // getBoundingClientRect().top + scrollY is its exact document offset; while it is stuck the
    // same sum reads back as scrollY, which only ever overestimates - and overestimating is the
    // safe direction here (a later collapse, never a wrong one). apply() corrects it exactly the
    // first time the page is near the top.
    let anchorTop = Math.max(0, Math.round(section.getBoundingClientRect().top + window.scrollY));
    let collapseAt = Infinity;
    let expandAt = Infinity;

    const refreshThresholds = () => {
      if (!rowHeight) {
        collapseAt = Infinity;
        expandAt = Infinity;
        return;
      }
      // The owner wants the row gone almost as soon as scrolling starts. The cost of
      // collapsing this early is a brief white band in the vacated strip until page content
      // has scrolled up to meet it (content only reaches the strip from
      // scrollY >= anchorTop + rowHeight); on this store's white page backgrounds it is
      // invisible, and it clears within the first hundred pixels of scroll anyway.
      expandAt = Math.round(anchorTop) + 4;
      collapseAt = Math.round(anchorTop) + 16;
    };

    // The row is the same height in every state now, so this can run at any moment and can never
    // cancel an animation. The old version removed the collapsed class, read offsetHeight and put
    // it back, which killed any transition in flight - and iOS Safari fires `resize` while
    // scrolling (URL bar), so it landed mid-collapse and snapped the row shut.
    const measure = () => {
      if (!mobile.matches) return;
      const height = search.getBoundingClientRect().height;
      if (height > 0) {
        rowHeight = height;
        // Published on <html>, not on <header>: .sticky-element and .menu-drawer are outside the
        // header and have to read it too.
        document.documentElement.style.setProperty('--search-row-height', `${height}px`);
      }
      refreshThresholds();
    };

    const setCollapsed = (next) => {
      collapsed = next;
      header.classList.toggle(COLLAPSED, next);
      document.documentElement.classList.toggle(ROOT_COLLAPSED, next);
      // The collapsed row is hidden by opacity only - visibility:hidden made
      // WebKit refuse every focus attempt from the reopening tap (it snapshots
      // focusability at gesture start), and a transform put the field mid-flight
      // at focus time, which made Safari reveal-scroll the whole header away
      // (see sections/header.liquid, rule 2). Tab order is managed here instead:
      // attributes carry no style-recalc dependency, so they cannot recreate
      // that refusal.
      search.querySelectorAll('input, button, a, select').forEach((el) => {
        el.tabIndex = next ? -1 : 0;
      });
    };

    const apply = () => {
      ticking = false;
      if (!header.isConnected) return; // a theme-editor section reload replaced us
      if (!mobile.matches || !rowHeight) return;
      if (performance.now() < latchedUntil) return;

      const y = window.scrollY;

      // Near the top the header cannot be stuck, and rect.top > 0 is exactly that test - so this
      // is where the pin offset can be read back exactly. Costs one layout read, only in the top
      // ~100px, never while an animation is latched.
      if (y < expandAt) {
        const top = section.getBoundingClientRect().top;
        if (top > 0) {
          const measured = Math.max(0, Math.round(top + y));
          if (measured !== anchorTop) {
            anchorTop = measured;
            refreshThresholds();
          }
        }
      }

      const next = collapsed ? y > expandAt : y > collapseAt;
      if (next === collapsed) return;

      setCollapsed(next);
      latchedUntil = performance.now() + DURATION + 60;
    };

    // Never write classes straight from the scroll event: batch into the frame that will paint them.
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };

    const closePredictiveSearch = () => {
      const predictive = header.querySelector('predictive-search');
      if (predictive && predictive.classList.contains('predictive-search-open')) {
        // The same two lines assets/search.js:22-23 runs when the menu drawer opens. Without them
        // the results panel keeps its own `visibility: visible` and would survive the row being
        // hidden.
        predictive.classList.remove('predictive-search-open');
        document.body.classList.remove('search-open');
      }
      const active = document.activeElement;
      if (active && header.contains(active) && typeof active.blur === 'function') active.blur();
    };

    const collapse = () => {
      if (!header.classList.contains(EXPANDED)) return;
      header.classList.remove(EXPANDED);
      pills.forEach((pill) => pill.setAttribute('aria-expanded', 'false'));
      closePredictiveSearch();
    };

    const expand = (pill) => {
      header.classList.add(EXPANDED);
      pills.forEach((other) =>
        other.setAttribute('aria-expanded', other === pill ? 'true' : 'false')
      );

      // Opening the row nudges the scroll position; re-baseline so the first finger scroll
      // is measured from here. (Programmatic scrolls are already ignored via touch intent.)
      lastY = window.scrollY;

      // No focus() here, deliberately. The pill is a <label for=input>: after this handler
      // returns, the browser's own post-click default focuses the field - the same final
      // focus-resolution step that used to clobber a manual focus now performs it, as a
      // genuine user-interaction focus that iOS raises the keyboard for. The EXPANDED class
      // added above makes the row interactive during dispatch, before that default runs.
      // The field is still fading in at that instant, which is fine: opacity never blocks
      // focus (WebKit's refusal is visibility-only), and the field's BOX is already exactly
      // where it will end up - it never moves, so Safari's focus reveal has nothing to
      // correct (sections/header.liquid, rule 2).
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    // Scrolling on is a clear signal the visitor has moved on from a search they opened -
    // but only FINGER scrolling. iOS itself scrolls the page when an input is focused
    // (scroll-into-view, keyboard adjustments), on its own animation schedule, which is
    // what made the reopened search "sometimes" close before the keyboard even appeared:
    // no settle window can outwait an animation with no fixed duration. Touch events
    // separate the two deterministically - Apple's scrolls arrive with no touchmove.
    // Deliberately touchmove and not touchstart: taps on the keyboard are touchstarts,
    // so typing can never register as scroll intent.
    let lastTouchMoveAt = 0;
    window.addEventListener('touchmove', () => { lastTouchMoveAt = performance.now(); }, { passive: true });
    window.addEventListener('wheel', () => { lastTouchMoveAt = performance.now(); }, { passive: true });

    window.addEventListener(
      'scroll',
      () => {
        if (!header.isConnected) return;
        if (!header.classList.contains(EXPANDED)) {
          lastY = window.scrollY;
          return;
        }
        // Not finger-driven: absorb it into the baseline so a later real scroll measures
        // from the settled position, and never close. Collapse() blurs the field, so when
        // a real scroll does close the search the keyboard drops with it.
        if (performance.now() - lastTouchMoveAt > 150) {
          lastY = window.scrollY;
          return;
        }
        if (Math.abs(window.scrollY - lastY) > 40) collapse();
      },
      { passive: true }
    );

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      // iOS fires resize whenever the URL bar shows or hides, which is during exactly the scroll
      // this feature reacts to. Only a width change can alter the row's height.
      if (window.innerWidth === lastWidth) return;
      lastWidth = window.innerWidth;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    });

    const syncMedia = () => {
      if (!header.isConnected) return;
      if (mobile.matches) {
        measure();
      } else {
        // Leave no collapsed state behind on desktop, where none of this CSS applies.
        if (collapsed) setCollapsed(false);
        collapse();
      }
    };
    if (typeof mobile.addEventListener === 'function') mobile.addEventListener('change', syncMedia);
    else if (typeof mobile.addListener === 'function') mobile.addListener(syncMedia);

    header.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') collapse();
    });

    header.addEventListener('focusout', (event) => {
      if (!header.classList.contains(EXPANDED)) return;
      if (event.relatedTarget && header.contains(event.relatedTarget)) return;
      if (event.relatedTarget === null) return;
      collapse();
    });

    // A web font landing after the first measurement changes the field's height without firing
    // resize, which would otherwise leave the published value permanently wrong.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure).catch(() => {});

    // Establish the arrival state with transitions suppressed: a restored scroll position would
    // otherwise animate the row away right after first paint.
    header.classList.add(NO_TRANSITION);
    measure();
    if (mobile.matches && rowHeight) setCollapsed(window.scrollY > collapseAt);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => header.classList.remove(NO_TRANSITION))
    );

    return {
      addPill(pill) {
        if (pills.indexOf(pill) !== -1) return;
        pills.push(pill);
        pill.setAttribute('aria-expanded', header.classList.contains(EXPANDED) ? 'true' : 'false');
        // The pill is a <label for=input>. The browser's post-click focus resolution always
        // gets the last word after JS handlers - on-device logs showed it clobbering any
        // focus set here (to the button, then to body once that default was suppressed).
        // A label turns that same final step into "focus the input": this handler opens the
        // row during dispatch, then the browser natively focuses the field - a genuine
        // user-interaction focus, which is what iOS raises the keyboard for. On the closing
        // tap, preventDefault stops that native focus from reopening the keyboard.
        pill.addEventListener('click', (event) => {
          // Keep the tap away from the theme's document-level handlers, which otherwise
          // treat it as an outside-click and close the search as it opens.
          event.stopPropagation();
          if (header.classList.contains(EXPANDED)) {
            event.preventDefault();
            collapse();
          } else {
            expand(pill);
          }
        });
      }
    };
  };

  const setup = (pill) => {
    if (pill.dataset.searchPillReady) return;
    const header = pill.closest('header');
    if (!header) return;

    let controller = controllers.get(header);
    if (!controller) {
      controller = createController(header);
      if (!controller) return;
      controllers.set(header, controller);
    }
    pill.dataset.searchPillReady = 'true';
    controller.addPill(pill);
  };

  const init = () => document.querySelectorAll('[data-search-pill]').forEach(setup);

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  init();
})();